import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { formatDate, statusColor, pluralize } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import {
    Inbox,
    CalendarClock,
    Wrench,
    ClipboardCheck,
    AlertTriangle,
    ArrowRight,
    Plus,
    ChevronDown,
    ChevronUp,
    Zap,
    DollarSign,
    UserPlus,
    FileWarning,
    ClipboardList,
    CreditCard,
    BanknoteX,
    BellRing,
    FileCheck,
    PackageSearch,
} from "lucide-react";
import { type LifecycleStageId, getStage } from "@/lib/lifecycle";
import { useActiveContext } from "@/lib/active-context";
import { useState } from "react";
import CommandCenterIntakeQueue from "./command-center-intake-queue";

interface CommandSummary {
    stages: Record<string, number>;
    at_risk: number;
    /** Distinct count of jobs scheduled before today, still active, never
     *  completed. Mirrors GET /api/dispatch/missed-jobs and feeds the
     *  loud "Missed Jobs" banner above the ActionCards. */
    missed?: { count: number; oldest_days: number };
    invoices_ready: number;
    compliance_overdue: number;
    // total / overdue_total are null when the viewer lacks finance.view — the
    // server redacts the dollars (customer money) but keeps the counts.
    outstanding_invoices?: {
        count: number;
        total: number | null;
        overdue_count?: number;
        overdue_total?: number | null;
    };
    /** Count of scheduled backflow jobs with an online jurisdiction but
     *  missing account number — they can't be submitted until the office
     *  fills in the reporting setup. Feeds the "Pre-Job Review" card. */
    pre_job_review?: number;
    pre_dispatch_payment?: number;
    /** Open declined charges / failed ACH debits awaiting office action.
     *  Feeds the "Failed Payments" card and the Money hub's Payments tab. */
    failed_payments?: number;
    /** Compliance safety net: jobs with an approaching/past due date that were
     *  never placed on the schedule. Feeds the red "Due Soon — Not Scheduled"
     *  card linking to the compliance roster tab. */
    due_soon_unscheduled?: number;
    /** Estimates the customer approved that have no follow-up job yet —
     *  this is new money, so the card sits in the TOP row. Cleared when a
     *  job is created from the estimate (jobs.source_estimate_id). */
    approved_estimates?: { count: number; oldest_days: number };
    /** Jobs scheduled inside the next 5 days whose PO isn't confirmed for
     *  delivery by job − buffer. Should be zero every morning — a nonzero
     *  number is a wasted truck roll waiting to happen. */
    parts_blocking_jobs?: number;
    recent_jobs: Array<{
        id: string;
        title: string;
        status: string;
        company_name: string;
        created_at: string;
    }>;
}

interface MissedJobSummary {
    id: string;
    title: string;
    siteName: string | null;
    siteCity: string | null;
    techName: string | null;
    daysMissed: number;
    scheduledStart: string | null;
}

const STATUS_COLORS: Record<string, string> = {
    scheduled: "#0284c7",
    in_progress: "#2563eb",
    completed: "#059669",
    cancelled: "#94a3b8",
    draft: "#64748b",
    office_review: "#7c3aed",
    awaiting_scheduling: "#d97706",
    en_route: "#0284c7",
};

const PIE_COLORS = ["#64748b", "#d97706", "#0284c7", "#2563eb", "#7c3aed", "#c8922a", "#059669"];

interface ActionCardProps {
    /** Lifecycle stage that supplies the default label/accent color and is
     *  written to the active context on click. Omit for non-lifecycle cards
     *  (e.g. Pre-Job Review) and supply `label`/`accentColor` instead. */
    stageId?: LifecycleStageId;
    /** Overrides the stage's branded label (required when stageId is omitted). */
    label?: string;
    /** Overrides the stage's accent color (used for the left border + icon). */
    accentColor?: string;
    count: number;
    icon: React.ComponentType<{ className?: string }>;
    route: string;
    actionLabel: string;
    subtitle?: string;
    urgent?: boolean;
}

function ActionCard({
    stageId,
    label,
    accentColor,
    count,
    icon: Icon,
    route,
    actionLabel,
    subtitle,
    urgent,
}: ActionCardProps) {
    const navigate = useNavigate();
    const { setContext } = useActiveContext();
    const stage = stageId ? getStage(stageId) : null;
    const accent = accentColor ?? stage?.hexColor ?? "#64748b";
    const displayLabel = label ?? stage?.brandedLabel ?? "";

    return (
        <Card
            className="relative overflow-hidden cursor-pointer group hover:shadow-[var(--shadow-card-hover)] transition-all duration-200 border-l-[3px]"
            style={{ borderLeftColor: urgent ? "#dc2626" : accent }}
            onClick={() => {
                if (stageId) setContext(stageId, "dashboard");
                navigate(route);
            }}
        >
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                            {displayLabel}
                        </p>
                        <p className="text-3xl font-bold tracking-tight tabular-nums">{count}</p>
                        {subtitle && <p className="text-xs text-foreground/80 leading-relaxed">{subtitle}</p>}
                    </div>
                    <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${accent}12`, color: accent }}
                    >
                        <Icon className="h-5 w-5" />
                    </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border/40">
                    <span className="text-xs font-semibold text-primary group-hover:underline inline-flex items-center gap-1 transition-colors">
                        {actionLabel}
                        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}

function DispatchCard({ count }: { count: number }) {
    const navigate = useNavigate();
    const { setContext } = useActiveContext();
    const stage = getStage("ready_to_schedule");

    const autoSched = useQuery({
        queryKey: ["dispatch", "autoschedule-settings"],
        queryFn: () =>
            api.get<{ enabled?: boolean }>("/api/dispatch/autoschedule/settings").catch(() => ({ enabled: false })),
        staleTime: 60_000,
    });
    const autoOn = autoSched.data?.enabled === true;

    return (
        <Card
            className={`relative overflow-hidden cursor-pointer group hover:shadow-[var(--shadow-card-hover)] transition-all duration-200 border-l-[3px] ${autoOn ? "opacity-80" : ""}`}
            style={{ borderLeftColor: stage.hexColor }}
            onClick={() => {
                setContext("ready_to_schedule", "dashboard");
                navigate("/dispatch?context=ready_to_schedule");
            }}
        >
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div className="space-y-1.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                            {stage.brandedLabel}
                        </p>
                        <p className="text-3xl font-bold tracking-tight tabular-nums">{count}</p>
                        {autoOn ? (
                            <p className="text-xs text-foreground/80 inline-flex items-center gap-1">
                                <Zap className="h-3 w-3 text-amber-500" />
                                Auto Scheduling Queue — {count} {pluralize(count, "job")}
                            </p>
                        ) : (
                            <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                Manual Scheduling Needed — {count} {pluralize(count, "job")}
                            </p>
                        )}
                    </div>
                    <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${stage.hexColor}12`, color: stage.hexColor }}
                    >
                        <CalendarClock className="h-5 w-5" />
                    </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border/40">
                    <span className="text-xs font-semibold text-primary group-hover:underline inline-flex items-center gap-1 transition-colors">
                        Open The Grid
                        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}

// ─── MissedJobsAlert (demoted) ────────────────────────────────────────
//
// The loud daily nag now lives in the AppShell's MissedJobsTopBar +
// MissedJobsTriageModal so it follows the office around the whole app
// instead of only firing on the dashboard. We keep a small inline
// reference here so the dashboard still acknowledges the missed-jobs
// signal — but it's a lightweight one-line link, not a dominant panel.
function MissedJobsAlert({
    count,
    oldestDays,
    jobs: _jobs,
}: {
    count: number;
    oldestDays: number;
    jobs: MissedJobSummary[];
}) {
    void _jobs;
    const navigate = useNavigate();
    if (count <= 0) return null;
    return (
        <button
            type="button"
            onClick={() => navigate("/dispatch?section=missed")}
            className="w-full inline-flex items-center justify-between gap-3 rounded-lg border border-red-300/70 bg-red-50/40 dark:bg-red-950/20 px-3 py-2 text-left hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
        >
            <span className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-300 shrink-0" />
                <span className="text-sm text-foreground truncate">
                    <strong className="font-semibold">
                        {count} missed job{count !== 1 ? "s" : ""}
                    </strong>{" "}
                    open from prior days
                    {oldestDays > 0 && ` · oldest ${oldestDays}d`}
                </span>
            </span>
            <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300">
                Triage
                <ArrowRight className="h-3 w-3" />
            </span>
        </button>
    );
}

export default function DashboardPage() {
    const { user, isAdmin, isOffice } = useAuth();
    const [perfOpen, setPerfOpen] = useState(false);

    const cmd = useQuery({
        queryKey: ["dashboard", "command-summary"],
        queryFn: () => api.get<CommandSummary>("/api/stats/command-summary"),
        enabled: isOffice || isAdmin,
    });

    // Pull the actual missed-job rows so the banner can list the oldest
    // three. Cheap query (count is in command-summary) but lets the
    // banner show "ACME — 2 days late" instead of just a number.
    const missedJobs = useQuery({
        queryKey: ["dashboard", "missed-jobs"],
        queryFn: () =>
            api.get<{
                jobs: MissedJobSummary[];
                counts: { total: number; oldestDays: number };
            }>("/api/dispatch/missed-jobs"),
        enabled: (isOffice || isAdmin) && (cmd.data?.missed?.count ?? 0) > 0,
    });

    const rateReviews = useQuery({
        queryKey: ["dashboard", "rate-reviews"],
        queryFn: () =>
            api.get<{
                reviews: Array<{ id: string; company_name: string; rate_expires_on: string; status: string }>;
            }>("/api/rate-cards/review-queue"),
        enabled: isAdmin,
    });

    // H20 / C5: surface the invoice attention worklist on Home and route to the
    // unified Collections hub so stuck money isn't buried in the Finance menu.
    const invoicesAttention = useQuery({
        queryKey: ["dashboard", "invoices-attention-count"],
        queryFn: () => api.get<{ count: number }>("/api/reports/invoices-attention?countOnly=1"),
        enabled: isOffice || isAdmin,
    });

    // H26: surface the city filing backlog (devices ready_to_file) on Home so
    // the legal filing step isn't forgotten after Approve & Send.
    const filingQueue = useQuery({
        queryKey: ["dashboard", "filing-queue-count"],
        queryFn: () => api.get<{ summary: { status: string; count: number }[] }>("/api/submissions/summary"),
        enabled: isOffice || isAdmin,
    });
    const readyToFileCount = filingQueue.data?.summary?.find((s) => s.status === "ready_to_file")?.count ?? 0;

    const loading = cmd.isLoading && (isOffice || isAdmin);
    if (loading) return <DashboardSkeleton />;

    const c = cmd.data;
    const stages = c?.stages ?? {};
    const recentJobs = c?.recent_jobs ?? [];

    const barData = Object.entries(stages)
        .filter(([, v]) => v > 0)
        .map(([status, count]) => ({
            name: status.replace(/_/g, " "),
            count,
            fill: STATUS_COLORS[status] ?? "#94a3b8",
        }));

    const pieData = barData.map((d) => ({ name: d.name, value: d.count }));

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Command Center</h1>
                    <p className="text-shell-foreground/80 text-sm mt-1">
                        What needs your attention, {user?.display_name?.split(" ")[0] ?? "User"}.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/customers/new"
                        className="hidden sm:inline-flex items-center gap-2 rounded-lg border border-input bg-background text-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:bg-accent transition-all"
                    >
                        <UserPlus className="h-4 w-4" />
                        New Customer
                    </Link>
                    <Link
                        to="/jobs/new"
                        className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:brightness-110 transition-all"
                    >
                        <Plus className="h-4 w-4" />
                        New Job
                    </Link>
                </div>
            </div>

            {cmd.isError && (
                <div className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100">
                    Could not load command summary. Check that you are signed in and the API is reachable.
                </div>
            )}

            <MissedJobsAlert
                count={c?.missed?.count ?? 0}
                oldestDays={c?.missed?.oldest_days ?? 0}
                jobs={missedJobs.data?.jobs ?? []}
            />

            {isAdmin && (rateReviews.data?.reviews?.length ?? 0) > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-2">
                            <ClipboardList className="h-5 w-5 text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                                    {rateReviews.data!.reviews.length} rate agreement
                                    {rateReviews.data!.reviews.length === 1 ? "" : "s"} need review
                                </p>
                                <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mt-0.5">
                                    Pricing stays in effect until you renew or adjust.
                                </p>
                            </div>
                        </div>
                        <Link
                            to="/rate-cards?reviewDue=true"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 dark:text-amber-100 hover:underline"
                        >
                            View roster
                            <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                </div>
            )}

            {/* ─── Pending Intake (PRIMARY) ─────────────────────────────
             *  The intake hub: incoming work orders, leads, bookings, and
             *  manual calls that still need to become jobs. This is now the
             *  top-of-page focus; the lifecycle stat cards below are a
             *  secondary at-a-glance summary. */}
            {(isOffice || isAdmin) && <CommandCenterIntakeQueue />}

            {/* ─── Lifecycle Overview (secondary) ───────────────────────── */}
            <div className="space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground/60">
                    Lifecycle Overview
                </h2>
                {/* ─── Lifecycle Action Cards (human-priority order) ────────── */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <ActionCard
                        stageId="intake"
                        count={stages.intake ?? 0}
                        icon={Inbox}
                        route="/jobs?view=lifecycle&stage=intake"
                        actionLabel="Process Intake"
                        subtitle="New requests awaiting review"
                    />
                    {/* APPROVED ESTIMATES — the other half of the magic-link
                     *  approve flow. New money outranks most cards; goes stale
                     *  fast, so it turns urgent after 3 days. */}
                    <ActionCard
                        label="Approved Estimates"
                        accentColor="#059669"
                        count={c?.approved_estimates?.count ?? 0}
                        icon={FileCheck}
                        route="/commercial?tab=approved_ready"
                        actionLabel="Schedule Now"
                        subtitle={
                            (c?.approved_estimates?.oldest_days ?? 0) > 3
                                ? `Approved work not yet scheduled · oldest ${c?.approved_estimates?.oldest_days}d`
                                : "Approved, not yet turned into jobs"
                        }
                        urgent={
                            (c?.approved_estimates?.oldest_days ?? 0) > 3 && (c?.approved_estimates?.count ?? 0) > 0
                        }
                    />
                    {/* PARTS BLOCKING JOBS (procurement Deploy 2) — jobs on the
                     *  board this week whose parts aren't confirmed in time.
                     *  Zero every morning or trucks roll without parts. */}
                    <ActionCard
                        label="Parts Blocking Jobs"
                        accentColor="#dc2626"
                        count={c?.parts_blocking_jobs ?? 0}
                        icon={PackageSearch}
                        route="/purchasing?filter=blocking"
                        actionLabel="Chase Parts"
                        subtitle="Scheduled ≤5 days, delivery not confirmed in time"
                        urgent={(c?.parts_blocking_jobs ?? 0) > 0}
                    />
                    <ActionCard
                        stageId="needs_review"
                        count={stages.needs_review ?? 0}
                        icon={ClipboardCheck}
                        route="/office-review"
                        actionLabel="Review Now"
                        subtitle="Completed work needing approval"
                    />
                    <ActionCard
                        stageId="at_risk"
                        count={(c?.at_risk ?? 0) + (c?.compliance_overdue ?? 0)}
                        icon={AlertTriangle}
                        route="/dispatch?context=at_risk"
                        actionLabel="View Risks"
                        subtitle="Overdue compliance & blocked jobs"
                        urgent={(c?.at_risk ?? 0) + (c?.compliance_overdue ?? 0) > 0}
                    />
                    <ActionCard
                        label="Due Soon — Not Scheduled"
                        accentColor="#dc2626"
                        count={c?.due_soon_unscheduled ?? 0}
                        icon={BellRing}
                        route="/scheduling/compliance-due?tab=due-soon-unscheduled"
                        actionLabel="Schedule Now"
                        subtitle="Compliance deadline near, no time on the board"
                        urgent={(c?.due_soon_unscheduled ?? 0) > 0}
                    />
                    <ActionCard
                        label="Pre-Job Review"
                        accentColor="#d97706"
                        count={c?.pre_job_review ?? 0}
                        icon={FileWarning}
                        route="/jobs/pre-job-review"
                        actionLabel="Review Setup"
                        subtitle="Jurisdiction/account needed before start"
                        urgent={(c?.pre_job_review ?? 0) > 0}
                    />
                    <ActionCard
                        label="Pre-Dispatch Payment"
                        accentColor="#dc2626"
                        count={c?.pre_dispatch_payment ?? 0}
                        icon={CreditCard}
                        route="/jobs/pre-dispatch-payment"
                        actionLabel="Collect Card"
                        subtitle="Card on file needed before dispatch"
                        urgent={(c?.pre_dispatch_payment ?? 0) > 0}
                    />
                    <ActionCard
                        label="Failed Payments"
                        accentColor="#dc2626"
                        count={c?.failed_payments ?? 0}
                        icon={BanknoteX}
                        route="/money?tab=payments"
                        actionLabel="Work Queue"
                        subtitle="Declined charges & failed ACH to retry"
                        urgent={(c?.failed_payments ?? 0) > 0}
                    />
                    <ActionCard
                        label="Needs Attention"
                        accentColor="#dc2626"
                        count={invoicesAttention.data?.count ?? 0}
                        icon={AlertTriangle}
                        route="/money"
                        actionLabel="Open the Money page"
                        subtitle="Invoices overdue, stuck, unsynced, or incomplete"
                        urgent={(invoicesAttention.data?.count ?? 0) > 0}
                    />
                    {readyToFileCount > 0 && (
                        <ActionCard
                            label="Ready to File"
                            accentColor="#d97706"
                            count={readyToFileCount}
                            icon={FileCheck}
                            route="/submissions?status=ready_to_file"
                            actionLabel="City Filing Queue"
                            subtitle="Tested devices awaiting city/water-district filing"
                            urgent
                        />
                    )}
                    <ActionCard
                        stageId="outstanding"
                        count={c?.outstanding_invoices?.count ?? 0}
                        icon={DollarSign}
                        route={
                            (c?.outstanding_invoices?.overdue_count ?? 0) > 0
                                ? "/money?tab=invoices"
                                : "/commercial?tab=outstanding"
                        }
                        actionLabel={
                            (c?.outstanding_invoices?.overdue_count ?? 0) > 0 ? "Review Aging" : "View Outstanding"
                        }
                        subtitle={
                            // Dollars are hidden (null) for viewers without finance.view —
                            // fall back to count-only phrasing so a "no money" rung still
                            // sees how many invoices need chasing, just not the amounts.
                            (c?.outstanding_invoices?.overdue_count ?? 0) > 0
                                ? c?.outstanding_invoices?.overdue_total != null
                                    ? `${c?.outstanding_invoices?.overdue_count} overdue · $${Number(c.outstanding_invoices.overdue_total).toLocaleString()} past due`
                                    : `${c?.outstanding_invoices?.overdue_count} overdue`
                                : c?.outstanding_invoices?.total != null
                                  ? c.outstanding_invoices.total > 0
                                      ? `$${Number(c.outstanding_invoices.total).toLocaleString()} outstanding`
                                      : "All invoices paid"
                                  : (c?.outstanding_invoices?.count ?? 0) > 0
                                    ? `${c?.outstanding_invoices?.count} outstanding`
                                    : "All invoices paid"
                        }
                        urgent={(c?.outstanding_invoices?.overdue_count ?? 0) > 0}
                    />
                    <DispatchCard count={stages.ready_to_schedule ?? 0} />
                    <ActionCard
                        stageId="in_progress"
                        count={stages.in_progress ?? 0}
                        icon={Wrench}
                        route="/dispatch?context=in_progress"
                        actionLabel="Track Field Work"
                        subtitle="Techs actively on a job"
                    />
                </div>
            </div>

            {/* ─── Recent Jobs ───────────────────────────────────────── */}
            <Card className="shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
                        Recent Jobs
                    </CardTitle>
                    <Link
                        to="/jobs"
                        className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 font-medium transition-colors"
                    >
                        View all <ArrowRight className="h-3 w-3" />
                    </Link>
                </CardHeader>
                <CardContent>
                    {recentJobs.length === 0 ? (
                        <div className="py-10 text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                                <Inbox className="h-6 w-6 text-primary" />
                            </div>
                            <p className="text-foreground/80 text-sm">
                                No jobs yet. When work comes in, it will appear here.
                            </p>
                            <Link
                                to="/jobs/new"
                                className="text-sm text-primary hover:underline mt-2 inline-block font-medium"
                            >
                                Create a job
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {recentJobs.map((job) => (
                                <Link
                                    key={job.id}
                                    to={`/jobs/${job.id}`}
                                    className="flex items-center justify-between rounded-lg border border-transparent p-2.5 hover:bg-secondary/60 hover:border-border/50 transition-all"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm truncate">{job.title}</p>
                                        <p className="text-xs text-foreground/80">
                                            {job.company_name ? `${job.company_name} · ` : ""}
                                            {formatDate(job.created_at)}
                                        </p>
                                    </div>
                                    <Badge className={statusColor(job.status)} variant="outline">
                                        {job.status?.replace(/_/g, " ")}
                                    </Badge>
                                </Link>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ─── Performance (collapsible) ─────────────────────────── */}
            {(isOffice || isAdmin) && (
                <div>
                    <button
                        type="button"
                        onClick={() => setPerfOpen(!perfOpen)}
                        className="flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground transition-colors"
                    >
                        {perfOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        Performance Charts
                    </button>

                    {perfOpen && (
                        <div className="grid gap-4 lg:grid-cols-2 mt-4">
                            <Card className="shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
                                        Jobs by Stage
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {barData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={240}>
                                            <BarChart data={barData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                                <XAxis
                                                    dataKey="name"
                                                    fontSize={11}
                                                    tick={{ fill: "var(--color-muted-foreground)" }}
                                                    axisLine={{ stroke: "var(--color-border)" }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    fontSize={11}
                                                    tick={{ fill: "var(--color-muted-foreground)" }}
                                                    axisLine={{ stroke: "var(--color-border)" }}
                                                    tickLine={false}
                                                />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: "var(--color-card)",
                                                        border: "1px solid var(--color-border)",
                                                        borderRadius: "8px",
                                                        fontSize: "12px",
                                                    }}
                                                />
                                                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                                    {barData.map((entry, i) => (
                                                        <Cell key={i} fill={entry.fill} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex h-[240px] items-center justify-center px-4 text-center text-sm text-foreground/80">
                                            No job volume by status yet.
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
                                        Stage Distribution
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {pieData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={240}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={65}
                                                    outerRadius={95}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                    label={
                                                        ((props: Record<string, any>) =>
                                                            `${props.name} ${((props.percent ?? 0) * 100).toFixed(0)}%`) as any
                                                    }
                                                >
                                                    {pieData.map((_, i) => (
                                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: "var(--color-card)",
                                                        border: "1px solid var(--color-border)",
                                                        borderRadius: "8px",
                                                        fontSize: "12px",
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex h-[240px] items-center justify-center px-4 text-center text-sm text-foreground/80">
                                            No status mix yet.
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
